import { useTranslation } from 'react-i18next'

import { NOTIFICATION_TYPE } from '../../../constants/common'
import { CalendarGrid } from '../../CalendarGrid'
import { NotificationInline } from '../../Notification'
import { CALENDAR_TASKS_FILTERS } from '../Calendar.constants'
import { CalendarContext } from '../Calendar.contexts'
import { CalendarDayPopoverActions } from '../CalendarDayPopoverActions'
import { CalendarHeaderActionsPortal } from '../CalendarHeaderActionsPortal'
import { CalendarItemCard } from '../CalendarItemCard'
import { CalendarItemStatus } from '../CalendarItemStatus'
import { CalendarItemTag } from '../CalendarItemTag'

import {
  QA_NAME_CALENDAR_NO_TASK_TYPES,
  QA_NAME_CALENDAR_TASK_NOTE,
} from './CalendarView.constants'
import { useCalendarView } from './CalendarView.hooks'
import {
  getCalendarViewBodyMessage,
  getCalendarViewNoTaskTypesNoticeProps,
  getHasCalendarViewAppointments,
  getHasCalendarViewNoTaskTypesNotice,
} from './CalendarView.utils'
import { CalendarViewNewMenu } from './CalendarViewNewMenu'

import * as styles from './CalendarView.module.css'

function CalendarView({
  canCreateAppointments,
  className = styles.CalendarView,
  filters = CALENDAR_TASKS_FILTERS,
  initialMode,
  modes,
  onChangeMode,
}) {
  const { t } = useTranslation()

  const {
    calendar,
    canOpenSettings,
    handleChangeTaskStatus,
    handleClickEmptyCell,
    handleClickItem,
    handleMoveItem,
    handleOpenAppointmentCreate,
    handleOpenRelieveDay,
    handleOpenSettings,
    handleOpenTask,
    handleOpenTaskCreate,
    handleStartAction,
    isTaskNoteShown,
    modal,
  } = useCalendarView({ filters, initialMode, modes, onChangeMode })

  return (
    <CalendarContext
      value={{
        onChangeTaskStatus: handleChangeTaskStatus,
        onOpenRelieveDay: handleOpenRelieveDay,
        onOpenTask: handleOpenTask,
      }}
    >
      <div className={className} onClickCapture={handleStartAction}>
        <CalendarHeaderActionsPortal>
          <CalendarViewNewMenu
            onSelectAppointment={handleOpenAppointmentCreate}
            onSelectTask={handleOpenTaskCreate}
          />
        </CalendarHeaderActionsPortal>
        {isTaskNoteShown ? (
          <NotificationInline
            qaName={QA_NAME_CALENDAR_TASK_NOTE}
            text={t('Calendar.TaskNotInCalendarNoteKey')}
            type={NOTIFICATION_TYPE.info}
          />
        ) : null}
        <CalendarGrid
          DayPopoverActions={CalendarDayPopoverActions}
          ItemCard={CalendarItemCard}
          ItemStatus={CalendarItemStatus}
          ItemTag={CalendarItemTag}
          bodyMessage={getCalendarViewBodyMessage({
            calendar,
            canOpenSettings,
            filters,
            t,
            onOpenSettings: handleOpenSettings,
          })}
          calendar={calendar}
          hasTimedItems={getHasCalendarViewAppointments(filters)}
          isLoading={calendar.isLoading || calendar.isRefetching}
          notice={
            getHasCalendarViewNoTaskTypesNotice({ calendar, filters }) ? (
              <NotificationInline
                {...getCalendarViewNoTaskTypesNoticeProps({
                  canOpenSettings,
                  t,
                  onOpenSettings: handleOpenSettings,
                })}
                qaName={QA_NAME_CALENDAR_NO_TASK_TYPES}
                type={NOTIFICATION_TYPE.info}
              />
            ) : null
          }
          onClickEmptyCell={
            canCreateAppointments ? handleClickEmptyCell : undefined
          }
          onClickItem={handleClickItem}
          onMoveItem={handleMoveItem}
          onSelectEmptyDays={
            canCreateAppointments ? handleOpenAppointmentCreate : undefined
          }
          onSelectEmptyRange={
            canCreateAppointments ? handleOpenAppointmentCreate : undefined
          }
        />
      </div>
      {modal}
    </CalendarContext>
  )
}

export default CalendarView
